import React, { useState } from 'react'

import {
    makeStyles,
    Theme,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Button,
    Checkbox,
    Popover,
    IconButton,
    Collapse,
    Typography,
    Badge,
    Tooltip,
} from '@material-ui/core'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import ExpandLessIcon from '@material-ui/icons/ExpandLess'
import EditIcon from '@material-ui/icons/Edit'

import { Selector } from '../../components/Selector'
import { PeriodOption } from '../../models/Frequency/types'
import { InspectionType, AreaConfig } from '../../models/Config/types'
import { Unit } from '../../models'
import { CreateAreaConfigForm } from './CreateAreaConfigForm'
import { CreateAreaConfigRequest } from '../../store'
import { InventorySelector } from './InventorySelector'
import {
    FIRE_ALARM,
    FIRE_EXT,
    GROUNDS,
    BACKFLOW,
    LIGHTS,
} from '../../hooks/useFacilitiesPlan/useFacilitiesPlan'
import {
    FacilityConfigs,
    FacilityConfig,
    AreaInventoryConfig,
} from '../../hooks/useFacilitiesPlan/types'
import { PERIOD_LABELS } from '../../hooks/useFacilitiesPlan/useFacilitiesPlan'

const PERIOD_OPTIONS = Object.entries(PeriodOption).map(
    ([key, value], index) => ({
        id: index,
        name: PERIOD_LABELS[value],
        value,
    }),
)

const FACILITY_TYPES = [
    'grounds',
    'fireExt',
    'fireAlarm',
    'backflow',
    'lights',
] as const

const FACILITY_LABELS = {
    grounds: 'Grounds',
    fireExt: 'Fire Ext.',
    fireAlarm: 'Fire Alarm',
    backflow: 'Backflow',
    lights: 'Lights',
}

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 350px)',
    },
    content: {
        flexDirection: 'column',
        overflowY: 'scroll',
        maxHeight: 'calc(100vh - 395px)',
        minHeight: 'calc(100vh - 395px)',
    },
    configSection: {
        borderBottom: `1px solid ${theme.palette.divider}`,
    },
    configHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: theme.spacing(1),
        backgroundColor: theme.palette.grey[100],
    },
    configTable: {
        '& .MuiTableCell-root': {
            padding: theme.spacing(1),
        },
    },
    tableContainer: {
        borderBottom: `1px solid ${theme.palette.divider}`,
    },
    headerCell: {
        backgroundColor: theme.palette.grey[100],
        fontWeight: 'bold',
        padding: theme.spacing(1),
        whiteSpace: 'nowrap',
        position: 'sticky',
        top: 0,
        zIndex: 1,
    },
    tableRow: {
        '&:nth-of-type(odd)': {
            backgroundColor: theme.palette.action.hover,
        },
    },
    cell: {
        padding: theme.spacing(1),
        whiteSpace: 'nowrap',
    },
    areaNameCell: {
        minWidth: 200,
    },
    checkboxCell: {
        minWidth: 100,
        textAlign: 'center',
    },
    footer: {
        borderTop: `1px solid ${theme.palette.divider}`,
        padding: theme.spacing(2),
        display: 'flex',
        justifyContent: 'flex-end',
        backgroundColor: theme.palette.background.paper,
    },
    submitButton: {
        backgroundColor: theme.palette.primary.main,
        '&:hover': {
            backgroundColor: theme.palette.primary.dark,
        },
    },
    addConfigButton: {
        padding: theme.spacing(1),
        borderTop: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.grey[50],
    },
    selectorCell: {
        minWidth: 180,
        '& .MuiOutlinedInput-root': {
            '& fieldset': {
                borderColor: theme.palette.grey[300],
            },
        },
    },
    facilityTypeCell: {
        width: '120px',
        fontWeight: 'bold',
    },
    instructionText: {
        padding: theme.spacing(1),
        color: theme.palette.text.secondary,
        fontSize: '0.875rem',
    },
    disabledCell: {
        backgroundColor: theme.palette.action.disabledBackground,
        cursor: 'not-allowed',
    },
    badge: {
        '& .MuiBadge-badge': {
            right: -3,
            top: 3,
            border: `2px solid ${theme.palette.background.paper}`,
            padding: '0 4px',
        },
    },
}))

interface AreaFacility {
    id: number
    areaConfig: AreaConfig
    grounds: boolean
    fireExt: boolean
    fireAlarm: boolean
    backflow: boolean
    lights: boolean
}

interface NewAreaFacilitiesProps {
    apartmentId: number
    facilityConfigs: FacilityConfigs
    newAreaConfigList: AreaConfig[]
    availableInspectionTypes: InspectionType[]
    inspectionUnits: Unit[]
    onNewAreaFacilityChange: (
        facilityType: keyof FacilityConfigs,
        update: Partial<{
            unit: Unit | null
            inspectionType: InspectionType | null
            period: PeriodOption
        }>,
    ) => void
    onAddNewAreaConfig: (
        facilityType: keyof FacilityConfigs,
        areaConfigs: AreaInventoryConfig[],
    ) => void
    onSubmit: () => void
    createAreaConfig: (
        request: CreateAreaConfigRequest,
        apartmentId: number,
    ) => Promise<AreaConfig>
}

export const NewAreaFacilities: React.FC<NewAreaFacilitiesProps> = ({
    apartmentId,
    facilityConfigs,
    newAreaConfigList,
    availableInspectionTypes = [],
    inspectionUnits = [],
    onNewAreaFacilityChange,
    onAddNewAreaConfig,
    onSubmit,
    createAreaConfig,
}) => {
    const classes = useStyles()
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)
    const [isConfigExpanded, setIsConfigExpanded] = useState(true)
    const [
        inventoryAnchorEl,
        setInventoryAnchorEl,
    ] = useState<HTMLElement | null>(null)
    const [
        selectedFacility,
        setSelectedFacility,
    ] = useState<FacilityConfig | null>(null)
    const [selectedAreaConfigId, setSelectedAreaConfigId] = useState<
        number | null
    >(null)
    const [selectedFacilityType, setSelectedFacilityType] = useState<
        keyof FacilityConfigs | null
    >(null)

    const handleAddAreaConfigClick = (
        event: React.MouseEvent<HTMLButtonElement>,
    ) => {
        setAnchorEl(event.currentTarget)
    }

    const handleAddAreaConfigClose = () => {
        setAnchorEl(null)
    }

    const open = Boolean(anchorEl)

    const handleInventoryClick = (
        event: React.MouseEvent<HTMLElement>,
        facilityConfig: FacilityConfig,
        facilityType: keyof FacilityConfigs,
        areaConfigId: number,
    ) => {
        setSelectedFacility(facilityConfig)
        setSelectedAreaConfigId(areaConfigId)
        setSelectedFacilityType(facilityType)
        setInventoryAnchorEl(event.currentTarget)
    }

    const handleInventoryClose = () => {
        setInventoryAnchorEl(null)
        setSelectedFacility(null)
        setSelectedFacilityType(null)
        setSelectedAreaConfigId(null)
    }

    const handleSetInventory = (selectedIds: number[]) => {
        if (selectedFacility && selectedFacilityType && selectedAreaConfigId) {
            // Create a new array instead of modifying the existing one
            const currentAreaConfigs = [...selectedFacility.areaConfigs]
            const areaConfigIndex = currentAreaConfigs.findIndex(
                (ac) => ac.area_config_id === selectedAreaConfigId,
            )

            let newAreaConfigs
            if (areaConfigIndex !== -1) {
                // Create a new array with the updated config
                newAreaConfigs = currentAreaConfigs.map((ac, index) =>
                    index === areaConfigIndex
                        ? { ...ac, inventory_config_ids: selectedIds }
                        : ac,
                )
            } else {
                // Add new config to a new array
                newAreaConfigs = [
                    ...currentAreaConfigs,
                    {
                        area_config_id: selectedAreaConfigId,
                        inventory_config_ids: selectedIds,
                    },
                ]
            }

            onAddNewAreaConfig(selectedFacilityType, newAreaConfigs)
        }
    }

    const lightInventoryCount = (areaConfigId: number) => {
        const areaConfig = facilityConfigs.lights.areaConfigs.find(
            (ac) => ac.area_config_id === areaConfigId,
        )
        // Only return the length if we have inventory_config_ids and they are not empty
        return areaConfig?.inventory_config_ids?.length || null
    }

    const isColumnEnabled = (facilityType: keyof typeof FACILITY_LABELS) => {
        const config = facilityConfigs[facilityType]
        return config.unit !== null && config.inspectionType !== null
    }

    const isCheckboxChecked = (
        facilityType: keyof typeof FACILITY_LABELS,
        areaConfigId: number,
    ) => {
        let selcted = false
        const found = facilityConfigs[facilityType].areaConfigs.find(
            (ac) => ac.area_config_id === areaConfigId,
        )
        if (found) {
            selcted = true
        }
        return selcted
    }

    const handleCheckboxChange = (
        facilityType: keyof typeof FACILITY_LABELS,
        areaConfigId: number,
    ) => {
        // Create a new array instead of referencing the existing one
        const currentAreaConfigs = [
            ...facilityConfigs[facilityType].areaConfigs,
        ]
        const areaConfig = currentAreaConfigs.find(
            (ac) => ac.area_config_id === areaConfigId,
        )

        let newAreaConfigs
        if (areaConfig) {
            newAreaConfigs = currentAreaConfigs.filter(
                (ac) => ac.area_config_id !== areaConfigId,
            )
        } else {
            const inventoryConfigIds = facilityConfigs[
                facilityType
            ].inspectionType?.inventory_configs?.map((ic) => ic.id)
            newAreaConfigs = [
                ...currentAreaConfigs,
                {
                    area_config_id: areaConfigId,
                    inventory_config_ids: inventoryConfigIds || [],
                },
            ]
        }
        onAddNewAreaConfig(facilityType, newAreaConfigs)
    }

    return (
        <Paper className={classes.root}>
            <div className={classes.content}>
                <Typography className={classes.instructionText}>
                    1. Configure your units and inspection types
                </Typography>
                <div className={classes.configSection}>
                    <div className={classes.configHeader}>
                        <span>Facility Configurations</span>
                        <IconButton
                            size="small"
                            onClick={() =>
                                setIsConfigExpanded(!isConfigExpanded)
                            }
                        >
                            {isConfigExpanded ? (
                                <ExpandLessIcon />
                            ) : (
                                <ExpandMoreIcon />
                            )}
                        </IconButton>
                    </div>
                    <Collapse in={isConfigExpanded}>
                        <TableContainer className={classes.configTable}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell
                                            className={`${classes.headerCell} ${classes.facilityTypeCell}`}
                                        >
                                            Facility Type
                                        </TableCell>
                                        <TableCell
                                            className={classes.headerCell}
                                        >
                                            Unit
                                        </TableCell>
                                        <TableCell
                                            className={classes.headerCell}
                                        >
                                            Inspection Type
                                        </TableCell>
                                        <TableCell
                                            className={classes.headerCell}
                                        >
                                            Frequency Period
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {FACILITY_TYPES.map((facilityType) => (
                                        <TableRow
                                            key={facilityType}
                                            className={classes.tableRow}
                                        >
                                            <TableCell
                                                className={`${classes.cell} ${classes.facilityTypeCell}`}
                                            >
                                                {FACILITY_LABELS[facilityType]}
                                            </TableCell>
                                            <TableCell
                                                className={`${classes.cell} ${classes.selectorCell}`}
                                            >
                                                <Selector
                                                    customStyle={{
                                                        select: {
                                                            width: '100%',
                                                            minWidth: 180,
                                                            textAlign: 'left',
                                                            fontSize:
                                                                '0.875rem',
                                                        },
                                                        menuItem: {
                                                            justifyContent:
                                                                'flex-start',
                                                            fontSize:
                                                                '0.875rem',
                                                        },
                                                    }}
                                                    currentValue={
                                                        facilityConfigs[
                                                            facilityType
                                                        ].unit?.id || -1
                                                    }
                                                    onChange={(e) => {
                                                        const selectedUnit =
                                                            inspectionUnits.find(
                                                                (u) =>
                                                                    u.id ===
                                                                    e.target
                                                                        .value,
                                                            ) || null
                                                        onNewAreaFacilityChange(
                                                            facilityType,
                                                            {
                                                                unit: selectedUnit,
                                                            },
                                                        )
                                                    }}
                                                    data={inspectionUnits}
                                                    getDisplayString={(unit) =>
                                                        unit.name
                                                    }
                                                    variant="outlined"
                                                    size="small"
                                                    searchable
                                                    noSelectionLabel="Select Unit"
                                                />
                                            </TableCell>
                                            <TableCell
                                                className={`${classes.cell} ${classes.selectorCell}`}
                                            >
                                                <Selector
                                                    customStyle={{
                                                        select: {
                                                            width: '100%',
                                                            minWidth: 180,
                                                            textAlign: 'left',
                                                            fontSize:
                                                                '0.875rem',
                                                        },
                                                        menuItem: {
                                                            justifyContent:
                                                                'flex-start',
                                                            fontSize:
                                                                '0.875rem',
                                                        },
                                                    }}
                                                    currentValue={
                                                        facilityConfigs[
                                                            facilityType
                                                        ].inspectionType?.id ||
                                                        -1
                                                    }
                                                    onChange={(e) => {
                                                        const selectedType =
                                                            availableInspectionTypes.find(
                                                                (t) =>
                                                                    t.id ===
                                                                    e.target
                                                                        .value,
                                                            ) || null
                                                        onNewAreaFacilityChange(
                                                            facilityType,
                                                            {
                                                                inspectionType: selectedType,
                                                            },
                                                        )
                                                    }}
                                                    data={
                                                        availableInspectionTypes
                                                    }
                                                    getDisplayString={(type) =>
                                                        type.name
                                                    }
                                                    variant="outlined"
                                                    size="small"
                                                    searchable
                                                    noSelectionLabel="Select Type"
                                                />
                                            </TableCell>
                                            <TableCell
                                                className={`${classes.cell} ${classes.selectorCell}`}
                                            >
                                                <Selector
                                                    customStyle={{
                                                        select: {
                                                            width: '100%',
                                                            minWidth: 150,
                                                            textAlign: 'left',
                                                            fontSize:
                                                                '0.875rem',
                                                        },
                                                        menuItem: {
                                                            justifyContent:
                                                                'flex-start',
                                                            fontSize:
                                                                '0.875rem',
                                                        },
                                                    }}
                                                    currentValue={
                                                        PERIOD_OPTIONS.find(
                                                            (p) =>
                                                                p.value ===
                                                                facilityConfigs[
                                                                    facilityType
                                                                ].period,
                                                        )?.id || 0
                                                    }
                                                    onChange={(e) => {
                                                        const option = PERIOD_OPTIONS.find(
                                                            (p) =>
                                                                p.id ===
                                                                e.target.value,
                                                        )
                                                        if (option) {
                                                            onNewAreaFacilityChange(
                                                                facilityType,
                                                                {
                                                                    period:
                                                                        option.value,
                                                                },
                                                            )
                                                        }
                                                    }}
                                                    data={PERIOD_OPTIONS}
                                                    getDisplayString={(item) =>
                                                        item.name
                                                    }
                                                    variant="outlined"
                                                    size="small"
                                                />
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Collapse>
                </div>

                <Typography className={classes.instructionText}>
                    2. Add your areas and select which units to apply
                </Typography>
                <TableContainer className={classes.tableContainer}>
                    <Table stickyHeader size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell
                                    className={`${classes.headerCell} ${classes.areaNameCell}`}
                                >
                                    Area Name
                                </TableCell>
                                {FACILITY_TYPES.map((facilityType) => (
                                    <TableCell
                                        key={facilityType}
                                        className={`${classes.headerCell} ${
                                            classes.checkboxCell
                                        } ${
                                            !isColumnEnabled(facilityType)
                                                ? classes.disabledCell
                                                : ''
                                        }`}
                                    >
                                        <Tooltip
                                            title={
                                                !isColumnEnabled(facilityType)
                                                    ? 'Please select both unit and inspection type in configuration'
                                                    : ''
                                            }
                                        >
                                            <span>
                                                {FACILITY_LABELS[facilityType]}
                                            </span>
                                        </Tooltip>
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {newAreaConfigList.map((areaConfig, idx) => (
                                <TableRow
                                    key={areaConfig.id}
                                    className={classes.tableRow}
                                >
                                    {/* area config name */}
                                    <TableCell
                                        className={`${classes.cell} ${classes.areaNameCell}`}
                                    >
                                        {areaConfig.name}
                                    </TableCell>

                                    {/* grounds */}
                                    <TableCell
                                        className={`${classes.cell} ${
                                            classes.checkboxCell
                                        } ${
                                            !isColumnEnabled(GROUNDS)
                                                ? classes.disabledCell
                                                : ''
                                        }`}
                                    >
                                        <div
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                            }}
                                        >
                                            <Checkbox
                                                checked={isCheckboxChecked(
                                                    GROUNDS,
                                                    areaConfig.id,
                                                )}
                                                onChange={(e) =>
                                                    isColumnEnabled(GROUNDS) &&
                                                    handleCheckboxChange(
                                                        GROUNDS,
                                                        areaConfig.id,
                                                    )
                                                }
                                                color="primary"
                                                disabled={
                                                    !isColumnEnabled(GROUNDS)
                                                }
                                            />
                                        </div>
                                    </TableCell>
                                    {/* fire alarm */}
                                    <TableCell
                                        className={`${classes.cell} ${
                                            classes.checkboxCell
                                        } ${
                                            !isColumnEnabled(FIRE_ALARM)
                                                ? classes.disabledCell
                                                : ''
                                        }`}
                                    >
                                        <div
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                            }}
                                        >
                                            <Checkbox
                                                checked={isCheckboxChecked(
                                                    FIRE_ALARM,
                                                    areaConfig.id,
                                                )}
                                                onChange={(e) =>
                                                    isColumnEnabled(
                                                        FIRE_ALARM,
                                                    ) &&
                                                    handleCheckboxChange(
                                                        FIRE_ALARM,
                                                        areaConfig.id,
                                                    )
                                                }
                                                color="primary"
                                                disabled={
                                                    !isColumnEnabled(FIRE_ALARM)
                                                }
                                            />
                                        </div>
                                    </TableCell>
                                    {/* fire ext */}
                                    <TableCell
                                        className={`${classes.cell} ${
                                            classes.checkboxCell
                                        } ${
                                            !isColumnEnabled(FIRE_EXT)
                                                ? classes.disabledCell
                                                : ''
                                        }`}
                                    >
                                        <div
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                            }}
                                        >
                                            <Checkbox
                                                checked={isCheckboxChecked(
                                                    FIRE_EXT,
                                                    areaConfig.id,
                                                )}
                                                onChange={(e) =>
                                                    isColumnEnabled(FIRE_EXT) &&
                                                    handleCheckboxChange(
                                                        FIRE_EXT,
                                                        areaConfig.id,
                                                    )
                                                }
                                                color="primary"
                                                disabled={
                                                    !isColumnEnabled(FIRE_EXT)
                                                }
                                            />
                                        </div>
                                    </TableCell>
                                    {/* backflow */}
                                    <TableCell
                                        className={`${classes.cell} ${
                                            classes.checkboxCell
                                        } ${
                                            !isColumnEnabled(BACKFLOW)
                                                ? classes.disabledCell
                                                : ''
                                        }`}
                                    >
                                        <div
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                            }}
                                        >
                                            <Checkbox
                                                checked={isCheckboxChecked(
                                                    BACKFLOW,
                                                    areaConfig.id,
                                                )}
                                                onChange={(e) =>
                                                    isColumnEnabled(BACKFLOW) &&
                                                    handleCheckboxChange(
                                                        BACKFLOW,
                                                        areaConfig.id,
                                                    )
                                                }
                                                color="primary"
                                                disabled={
                                                    !isColumnEnabled(BACKFLOW)
                                                }
                                            />
                                        </div>
                                    </TableCell>
                                    {/* lights */}
                                    <TableCell
                                        className={`${classes.cell} ${
                                            classes.checkboxCell
                                        } ${
                                            !isColumnEnabled(GROUNDS)
                                                ? classes.disabledCell
                                                : ''
                                        }`}
                                    >
                                        <div
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                            }}
                                        >
                                            <Checkbox
                                                checked={isCheckboxChecked(
                                                    LIGHTS,
                                                    areaConfig.id,
                                                )}
                                                onChange={(e) =>
                                                    isColumnEnabled(GROUNDS) &&
                                                    handleCheckboxChange(
                                                        LIGHTS,
                                                        areaConfig.id,
                                                    )
                                                }
                                                color="primary"
                                                disabled={
                                                    !isColumnEnabled(GROUNDS)
                                                }
                                            />
                                            {isCheckboxChecked(
                                                LIGHTS,
                                                areaConfig.id,
                                            ) && (
                                                <Badge
                                                    badgeContent={lightInventoryCount(
                                                        areaConfig.id,
                                                    )}
                                                    color="primary"
                                                    className={classes.badge}
                                                >
                                                    <IconButton
                                                        size="small"
                                                        onClick={(e) =>
                                                            handleInventoryClick(
                                                                e,
                                                                facilityConfigs.lights,
                                                                LIGHTS,
                                                                areaConfig.id,
                                                            )
                                                        }
                                                    >
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                </Badge>
                                            )}
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                    <div className={classes.addConfigButton}>
                        <Button
                            variant="outlined"
                            color="primary"
                            onClick={handleAddAreaConfigClick}
                        >
                            + Add Area Config
                        </Button>
                    </div>
                </TableContainer>
            </div>

            <div className={classes.footer}>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={onSubmit}
                    className={classes.submitButton}
                >
                    CREATE FACILITIES PLAN
                </Button>
            </div>

            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handleAddAreaConfigClose}
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'center',
                }}
                transformOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center',
                }}
            >
                <CreateAreaConfigForm
                    onClose={handleAddAreaConfigClose}
                    createAreaConfig={createAreaConfig}
                    apartmentId={apartmentId}
                />
            </Popover>

            <Popover
                open={Boolean(inventoryAnchorEl)}
                anchorEl={inventoryAnchorEl}
                onClose={handleInventoryClose}
                anchorOrigin={{
                    vertical: 'center',
                    horizontal: 'center',
                }}
                transformOrigin={{
                    vertical: 'center',
                    horizontal: 'center',
                }}
            >
                {selectedFacility && selectedAreaConfigId && (
                    <InventorySelector
                        selectedFacility={selectedFacility}
                        areaConfig={
                            newAreaConfigList.find(
                                (ac) => ac.id === selectedAreaConfigId,
                            )!
                        }
                        onSelectionChange={(selectedIds) => {
                            handleSetInventory(selectedIds)
                        }}
                        onClose={handleInventoryClose}
                    />
                )}
            </Popover>
        </Paper>
    )
}
